export interface DebugConfig {
  enabled: boolean;
  level: 'error' | 'warn' | 'info' | 'debug' | 'trace';
  prefix?: string;
}

class DebugManager {
  private config: DebugConfig;
  

  constructor() {
    const debugModeEnv = import.meta.env.VITE_DEBUG_MODE;
    let enabled: boolean;
    
    if (debugModeEnv !== undefined) {
      enabled = debugModeEnv === 'true';
    } else {
      enabled = import.meta.env.DEV;
    }
    
    this.config = {
      enabled,
      level: 'debug',
      prefix: '🔍'
    };
    
  }

  public isEnabled(): boolean {
    return this.config.enabled;
  }

  private formatMessage(level: string, module: string | undefined, message: any): string {
    const timestamp = new Date().toLocaleTimeString();
    const prefix = this.config.prefix || '🔍';
    const moduleStr = module ? `[${module}]` : '';
    const levelStr = `[${level.toUpperCase()}]`;
    
    return `${prefix} ${timestamp} ${levelStr} ${moduleStr}`;
  }

  private shouldLog(level: DebugConfig['level']): boolean {
    const levels = ['error', 'warn', 'info', 'debug', 'trace'];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const msgLevelIndex = levels.indexOf(level);
    
    return msgLevelIndex <= currentLevelIndex;
  }

  private log(level: DebugConfig['level'], module: string | undefined, ...args: any[]): void {
    if (!this.config.enabled || !this.shouldLog(level)) {
      return;
    }

    const prefix = this.formatMessage(level, module, args[0]);
    
    switch (level) {
      case 'error':
        console.error(prefix, ...args);
        break;
      case 'warn':
        console.warn(prefix, ...args);
        break;
      case 'info':
        console.info(prefix, ...args);
        break;
      case 'debug':
        console.debug(prefix, ...args);
        break;
      case 'trace':
        console.trace(prefix, ...args);
        break;
    }
  }

  public error(module: string | undefined, ...args: any[]): void {
    this.log('error', module, ...args);
  }

  public warn(module: string | undefined, ...args: any[]): void {
    this.log('warn', module, ...args);
  }

  public info(module: string | undefined, ...args: any[]): void {
    this.log('info', module, ...args);
  }

  public debug(module: string | undefined, ...args: any[]): void {
    this.log('debug', module, ...args);
  }

  public trace(module: string | undefined, ...args: any[]): void {
    this.log('trace', module, ...args);
  }
}

const debugManager = new DebugManager();

export const createDebugger = (module: string) => {
  return {
    error: (...args: any[]) => debugManager.error(module, ...args),
    warn: (...args: any[]) => debugManager.warn(module, ...args),
    info: (...args: any[]) => debugManager.info(module, ...args),
    debug: (...args: any[]) => debugManager.debug(module, ...args),
    trace: (...args: any[]) => debugManager.trace(module, ...args),
  };
};

export const debug = {
  error: (...args: any[]) => debugManager.error(undefined, ...args),
  warn: (...args: any[]) => debugManager.warn(undefined, ...args),
  info: (...args: any[]) => debugManager.info(undefined, ...args),
  debug: (...args: any[]) => debugManager.debug(undefined, ...args),
  trace: (...args: any[]) => debugManager.trace(undefined, ...args),
  isEnabled: () => debugManager.isEnabled(),
};

export default debug; 